import React, { useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import Quagga from '@ericblade/quagga2';
import { EventScannerCodeType } from '../../generated/graphql';

interface Props {
  codeTypes: EventScannerCodeType[];
  isScanning: boolean;
  displayVideo: boolean;
  onScanCode: (code: string) => void;
  onVideoInitialized: (success: boolean) => void;
}

class SpacedAnimator {
  readonly interval: number;
  readonly exec: () => Promise<void>;
  lastTick = 0;
  private frame: number | null = null;

  constructor(interval: number, exec: () => Promise<void>) {
    this.interval = interval;
    this.exec = exec;
  }

  stop(): void {
    this.frame && cancelAnimationFrame(this.frame);
  }

  start(): void {
    this.frame = requestAnimationFrame((timestamp) => this.tick(timestamp));
  }

  private async tick(timestamp: number): Promise<void> {
    const step = timestamp - this.lastTick;
    if (step > this.interval) {
      this.lastTick = timestamp;
      try {
        await this.exec();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    this.start();
  }
}

function codeFromQR(imageData: ImageData): string | undefined {
  const result = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: 'dontInvert',
  });
  return result?.data;
}

async function codeFromEAN(imageUrl: string): Promise<string | undefined> {
  const result = await Quagga.decodeSingle({
    decoder: {
      readers: ['ean_reader'],
    },
    locate: true,
    src: imageUrl,
  });
  return result?.codeResult?.code || undefined;
}

const sizes = {
  shortScreen: {
    width: 345,
    height: 262,
  },
  regularScreen: {
    width: 345,
    height: 304,
  },
};

export function QRReader({ codeTypes, onScanCode, isScanning, onVideoInitialized, displayVideo }: Props): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContext = useRef<CanvasRenderingContext2D | null>(null);
  const isScanningRef = useRef(isScanning);
  isScanningRef.current = isScanning;
  const isDisplayingVideoRef = useRef(displayVideo);
  isDisplayingVideoRef.current = displayVideo;
  const drawRef = useRef(new SpacedAnimator(30, draw));
  const scanRef = useRef(new SpacedAnimator(250, scan));

  const onScanCodeRef = useRef(onScanCode);
  onScanCodeRef.current = onScanCode;

  async function draw(): Promise<void> {
    const video = videoRef.current;
    const canvasElement = canvasRef.current;
    if (!(video && canvasElement)) return;

    if (!canvasContext.current) {
      canvasContext.current = canvasElement.getContext('2d', { alpha: false });
    }

    const canvas = canvasContext.current;
    if (video.readyState === video.HAVE_ENOUGH_DATA && isDisplayingVideoRef.current && canvas !== null) {
      canvasElement.hidden = false;
      canvasElement.height = video.videoHeight;
      canvasElement.width = video.videoWidth;
      canvas.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
    } else {
      canvasElement.hidden = true;
    }
  }

  async function scan(): Promise<void> {
    const canvas = canvasRef.current;
    const context = canvasContext.current;
    if (!isScanningRef.current || context === null || canvas === null) return;

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const code =
      (codeTypes.includes('qr') && codeFromQR(imageData)) ||
      (codeTypes.includes('ean') && (await codeFromEAN(canvas.toDataURL('image/jpeg'))));
    if (code) {
      onScanCodeRef.current(code);
    }
  }

  const videoSize = {
    width: document.body.clientHeight > 600 ? sizes.regularScreen.width : sizes.shortScreen.width,
    height: document.body.clientHeight > 600 ? sizes.regularScreen.height : sizes.shortScreen.height,
  };

  useEffect(() => {
    const video = videoRef.current;
    let stream: MediaStream | null = null;
    const startStream = async (): Promise<void> => {
      if (video) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', ...videoSize },
          });
          video.srcObject = stream;
          video.play();
          onVideoInitialized(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          onVideoInitialized(false);
        }
      }
    };
    startStream();

    drawRef.current.start();
    scanRef.current.start();

    return () => {
      if (stream) stream.getTracks()[0].stop();
      drawRef.current.stop();
      scanRef.current.stop();
    };
  }, []);

  return (
    <>
      <video ref={videoRef} playsInline hidden />
      <canvas ref={canvasRef} />
    </>
  );
}
